import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { Save, ArrowLeft, Image as ImageIcon } from 'lucide-react'
import { useCategories } from '@/modules/categories-api'
import { useBrands, useCreateProduct, useUpdateProduct, useProducts } from '@/modules/products-api'
import { useToast } from '../../components/Toast'
import { uploadImage } from '@/modules/uploads-api'
import { Upload, Loader2 } from 'lucide-react'

function slugify(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd').replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '').slice(0, 80)
}

interface FormState {
  slug: string; name: string; brand: string
  price: number; oldPrice: number; discountPct: number
  image: string; imagesText: string
  rating: number; reviews: number; inStock: boolean
  shortDesc: string; description: string; specsText: string
  categoryId: string
}

const EMPTY: FormState = {
  slug: '', name: '', brand: 'VoltX',
  price: 0, oldPrice: 0, discountPct: 0,
  image: '', imagesText: '',
  rating: 4.5, reviews: 0, inStock: true,
  shortDesc: '', description: '', specsText: '',
  categoryId: '',
}

export function ProductFormPage() {
  const { id } = useParams<{ id?: string }>()
  const navigate = useNavigate()
  const push = useToast((s) => s.push)
  const [uploadingMain, setUploadingMain] = useState(false)
  const [uploadingExtra, setUploadingExtra] = useState(false)
  const { data: categories } = useCategories()
  const { data: brandList } = useBrands()
  const createMut = useCreateProduct()
  const updateMut = useUpdateProduct()

  const isEdit = Boolean(id && id !== 'moi')
  // Fetch product chi tiết nếu edit
  const { data: pageData } = useProducts({ limit: 200 })
  const editingProduct = isEdit ? pageData.items.find((p) => p.id === id) : undefined

  const [v, setV] = useState<FormState>(EMPTY)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [slugTouched, setSlugTouched] = useState(false)

  useEffect(() => {
    if (!isEdit) {
      if (categories.length > 0 && !v.categoryId) {
        setV((s) => ({ ...s, categoryId: categories[0].id }))
      }
      return
    }
    if (editingProduct) {
      setV({
        slug: editingProduct.slug, name: editingProduct.name, brand: editingProduct.brand,
        price: editingProduct.price, oldPrice: editingProduct.oldPrice, discountPct: editingProduct.discountPct,
        image: editingProduct.image,
        imagesText: (editingProduct.images ?? []).join('\n'),
        rating: editingProduct.rating, reviews: editingProduct.reviews, inStock: editingProduct.inStock,
        shortDesc: editingProduct.shortDesc ?? '', description: editingProduct.description ?? '',
        specsText: editingProduct.specs
          ? Object.entries(editingProduct.specs).map(([k, val]) => `${k}: ${val}`).join('\n')
          : '',
        categoryId: categories.find((c) => c.slug === editingProduct.categorySlug)?.id ?? '',
      })
      setSlugTouched(true)
    }
  }, [editingProduct, isEdit, categories]) // eslint-disable-line

  const upd = <K extends keyof FormState>(k: K, val: FormState[K]) => {
    setV((s) => ({ ...s, [k]: val }))
    setErrors((e) => ({ ...e, [k as string]: '' }))
  }

  useEffect(() => {
    if (!isEdit && !slugTouched && v.name) {
      setV((s) => ({ ...s, slug: slugify(v.name) }))
    }
  }, [v.name, isEdit, slugTouched])

  useEffect(() => {
    if (v.oldPrice > 0 && v.price > 0 && v.oldPrice > v.price) {
      const pct = Math.round(((v.oldPrice - v.price) / v.oldPrice) * 100)
      setV((s) => ({ ...s, discountPct: pct }))
    }
  }, [v.price, v.oldPrice])

  const busy = createMut.isPending || updateMut.isPending

  const inputCls = (err?: string) => [
    'w-full h-11 rounded-md border bg-surface px-3.5 text-sm outline-none transition',
    err ? 'border-danger focus:border-danger' : 'border-border focus:border-brand-500',
  ].join(' ')

  const handleMainUpload = async (file?: File) => {
    if (!file) return
    setUploadingMain(true)
    try {
      const url = await uploadImage(file)
      upd('image', url)
      if (!v.imagesText.trim()) upd('imagesText', url)
      push('success', 'Tải ảnh chính thành công')
    } catch (err: any) {
      push('error', err?.message ?? 'Upload thất bại')
    } finally {
      setUploadingMain(false)
    }
  }

  const handlePasteFiles = async (items: DataTransferItemList | null, onBatch: (urls: string[]) => void) => {
    if (!items) return
    const files: File[] = []
    for (let i = 0; i < items.length; i++) {
      const it = items[i]
      if (it.kind === 'file' && it.type.startsWith('image/')) {
        const f = it.getAsFile()
        if (f) files.push(f)
      }
    }
    if (files.length === 0) return
    try {
      const urls: string[] = []
      for (const f of files) {
        const url = await uploadImage(f)
        urls.push(url)
      }
      onBatch(urls)
      push('success', `Đã paste & tải ${urls.length} ảnh`)
    } catch (err: any) {
      push('error', err?.message ?? 'Paste upload thất bại')
    }
  }

  const onPasteMain = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const items = e.clipboardData?.items ?? null
    const hasImg = items && Array.from(items).some((i) => i.kind === 'file' && i.type.startsWith('image/'))
    if (!hasImg) return
    e.preventDefault()
    setUploadingMain(true)
    handlePasteFiles(items, (urls) => { if (urls[0]) { upd('image', urls[0]); if (!v.imagesText.trim()) upd('imagesText', urls[0]) } }).finally(() => setUploadingMain(false))
  }

  const onPasteExtra = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items ?? null
    const hasImg = items && Array.from(items).some((i) => i.kind === 'file' && i.type.startsWith('image/'))
    if (!hasImg) return
    e.preventDefault()
    setUploadingExtra(true)
    handlePasteFiles(items, (urls) => {
      const existing = v.imagesText.trim()
      const merged = (existing ? existing + '\n' : '') + urls.join('\n')
      upd('imagesText', merged)
    }).finally(() => setUploadingExtra(false))
  }

  const handleExtraUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return
    setUploadingExtra(true)
    try {
      const urls: string[] = []
      for (const f of Array.from(files)) {
        const url = await uploadImage(f)
        urls.push(url)
      }
      const existing = v.imagesText.trim()
      const merged = (existing ? existing + '\n' : '') + urls.join('\n')
      upd('imagesText', merged)
      push('success', `Đã tải ${urls.length} ảnh`)
    } catch (err: any) {
      push('error', err?.message ?? 'Upload thất bại')
    } finally {
      setUploadingExtra(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const errs: Record<string, string> = {}
    if (!v.name.trim())        errs.name = 'Vui lòng nhập tên'
    if (!v.slug.trim())        errs.slug = 'Slug không được rỗng'
    if (!v.image.trim())       errs.image = 'Cần ảnh chính'
    if (v.price <= 0)          errs.price = 'Giá phải lớn hơn 0'
    if (!v.categoryId)         errs.categoryId = 'Chọn danh mục'
    if (Object.keys(errs).length) { setErrors(errs); return }

    const images = v.imagesText.split('\n').map((s) => s.trim()).filter(Boolean)
    const specs: Record<string, string> = {}
    v.specsText.split('\n').forEach((line) => {
      const [k, ...rest] = line.split(':')
      if (k && rest.length > 0) specs[k.trim()] = rest.join(':').trim()
    })

    const payload = {
      slug: v.slug, name: v.name, brand: v.brand,
      price: v.price, oldPrice: v.oldPrice, discountPct: v.discountPct,
      image: v.image, images,
      rating: v.rating, reviews: v.reviews, inStock: v.inStock,
      shortDesc: v.shortDesc || undefined,
      description: v.description || undefined,
      specs: Object.keys(specs).length ? specs : undefined,
      categoryId: v.categoryId,
    }

    try {
      if (isEdit && editingProduct) {
        await updateMut.mutateAsync({ id: editingProduct.id, input: payload })
        push('success', `Đã cập nhật "${v.name}"`)
      } else {
        await createMut.mutateAsync(payload)
        push('success', `Đã thêm sản phẩm "${v.name}"`)
      }
      navigate('/admin/san-pham')
    } catch (e: any) {
      push('error', e?.message ?? 'Lỗi mạng')
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <Link to="/admin/san-pham" className="text-sm text-muted hover:text-brand-600 inline-flex items-center gap-1 mb-1">
            <ArrowLeft className="w-4 h-4" /> Danh sách sản phẩm
          </Link>
          <h1 className="text-2xl font-bold text-text">
            {isEdit ? 'Sửa sản phẩm' : 'Thêm sản phẩm mới'}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/san-pham" className="h-11 px-5 rounded-md border border-border text-text hover:bg-slate-50 font-medium text-sm">
            Hủy
          </Link>
          <button type="submit" disabled={busy} className="h-11 px-5 rounded-md bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 text-white font-semibold text-sm inline-flex items-center gap-2">
            <Save className="w-4 h-4" /> {busy ? 'Đang lưu...' : isEdit ? 'Lưu thay đổi' : 'Tạo sản phẩm'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-5">
        <div className="space-y-5">
          <section className="bg-surface border border-border rounded-xl shadow-card p-5 space-y-4">
            <h3 className="font-semibold text-text">Thông tin chung</h3>

            <label className="block">
              <div className="text-sm font-medium text-text mb-1.5">Tên sản phẩm <span className="text-danger">*</span></div>
              <input value={v.name} onChange={(e) => upd('name', e.target.value)} className={inputCls(errors.name)} placeholder="VD: Xe máy điện Alpha S" />
              {errors.name && <div className="text-xs text-danger mt-1">{errors.name}</div>}
            </label>

            <label className="block">
              <div className="text-sm font-medium text-text mb-1.5">Slug URL <span className="text-danger">*</span></div>
              <input
                value={v.slug}
                onChange={(e) => { setSlugTouched(true); upd('slug', slugify(e.target.value)) }}
                className={inputCls(errors.slug)}
                placeholder="xe-may-dien-alpha-s"
              />
              <div className="mt-2">
                <label className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-border bg-surface hover:bg-brand-50 hover:border-brand-500 cursor-pointer text-sm font-medium text-text transition">
                  {uploadingExtra ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Upload nhiều ảnh
                  <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleExtraUpload(e.target.files)} disabled={uploadingExtra} />
                </label>
              </div>
              <div className="text-xs text-muted mt-1">URL: <span className="font-mono">/san-pham/{v.slug || '...'}</span></div>
              {errors.slug && <div className="text-xs text-danger mt-1">{errors.slug}</div>}
            </label>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block">
                <div className="text-sm font-medium text-text mb-1.5">Danh mục <span className="text-danger">*</span></div>
                <select value={v.categoryId} onChange={(e) => upd('categoryId', e.target.value)} className={inputCls(errors.categoryId)}>
                  <option value="">— Chọn —</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
                {errors.categoryId && <div className="text-xs text-danger mt-1">{errors.categoryId}</div>}
              </label>
              <label className="block">
                <div className="text-sm font-medium text-text mb-1.5">Thương hiệu</div>
                <input value={v.brand} onChange={(e) => upd('brand', e.target.value)} className={inputCls()} list="brand-list" />
                <datalist id="brand-list">
                  {brandList.map((b) => <option key={b} value={b} />)}
                </datalist>
              </label>
            </div>

            <label className="block">
              <div className="text-sm font-medium text-text mb-1.5">Mô tả ngắn</div>
              <textarea
                value={v.shortDesc}
                onChange={(e) => upd('shortDesc', e.target.value)}
                rows={3}
                className="w-full rounded-md border border-border bg-surface px-3.5 py-2.5 text-sm outline-none focus:border-brand-500 resize-none"
                placeholder="Mô tả ngắn hiện ở trang sản phẩm..."
              />
            </label>

            <label className="block">
              <div className="text-sm font-medium text-text mb-1.5">Mô tả đầy đủ</div>
              <textarea
                value={v.description}
                onChange={(e) => upd('description', e.target.value)}
                rows={5}
                className="w-full rounded-md border border-border bg-surface px-3.5 py-2.5 text-sm outline-none focus:border-brand-500 resize-y"
              />
            </label>
          </section>

          <section className="bg-surface border border-border rounded-xl shadow-card p-5 space-y-4">
            <h3 className="font-semibold text-text">Giá & Khuyến mãi</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="block">
                <div className="text-sm font-medium text-text mb-1.5">Giá bán (đ) <span className="text-danger">*</span></div>
                <input type="number" value={v.price} onChange={(e) => upd('price', Number(e.target.value))} className={inputCls(errors.price)} />
                {errors.price && <div className="text-xs text-danger mt-1">{errors.price}</div>}
              </label>
              <label className="block">
                <div className="text-sm font-medium text-text mb-1.5">Giá gốc (đ)</div>
                <input type="number" value={v.oldPrice} onChange={(e) => upd('oldPrice', Number(e.target.value))} className={inputCls()} />
              </label>
              <label className="block">
                <div className="text-sm font-medium text-text mb-1.5">% Giảm (auto)</div>
                <input type="number" value={v.discountPct} onChange={(e) => upd('discountPct', Number(e.target.value))} className={inputCls()} />
              </label>
            </div>
          </section>

          <section className="bg-surface border border-border rounded-xl shadow-card p-5 space-y-4">
            <h3 className="font-semibold text-text">Thông số kỹ thuật</h3>
            <label className="block">
              <div className="text-sm font-medium text-text mb-1.5">Mỗi dòng 1 cặp "Tên: Giá trị"</div>
              <textarea
                value={v.specsText}
                onChange={(e) => upd('specsText', e.target.value)}
                rows={8}
                className="w-full rounded-md border border-border bg-surface px-3.5 py-2.5 text-sm font-mono outline-none focus:border-brand-500 resize-y"
                placeholder={`Động cơ: Bosch 1500W\nPin: Lithium 60V 20Ah\nQuãng đường: 70 km\nBảo hành: 3 năm`}
              />
            </label>
          </section>
        </div>

        <div className="space-y-5">
          <section className="bg-surface border border-border rounded-xl shadow-card p-5 space-y-4">
            <h3 className="font-semibold text-text">Trạng thái</h3>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={v.inStock}
                onChange={(e) => upd('inStock', e.target.checked)}
                className="w-5 h-5 accent-brand-600 rounded"
              />
              <div>
                <div className="text-sm font-medium text-text">Còn hàng</div>
                <div className="text-xs text-muted">Bỏ chọn nếu sản phẩm đang hết hàng</div>
              </div>
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <div className="text-sm font-medium text-text mb-1.5">Rating</div>
                <input type="number" step="0.1" min="0" max="5" value={v.rating} onChange={(e) => upd('rating', Number(e.target.value))} className={inputCls()} />
              </label>
              <label className="block">
                <div className="text-sm font-medium text-text mb-1.5">Lượt đánh giá</div>
                <input type="number" value={v.reviews} onChange={(e) => upd('reviews', Number(e.target.value))} className={inputCls()} />
              </label>
            </div>
          </section>

          <section
            className="bg-surface border border-border rounded-xl shadow-card p-5 space-y-4 focus:outline-none"
            tabIndex={0}
            onPaste={onPasteMain as any}
          >
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-text">Hình ảnh</h3>
              <span className="text-xs text-muted">Click vào khung → Ctrl+V để paste ảnh</span>
            </div>

            <label className="block">
              <div className="text-sm font-medium text-text mb-1.5">Ảnh chính (URL) <span className="text-danger">*</span></div>
              <div className="flex gap-2">
                <input value={v.image} onChange={(e) => upd('image', e.target.value)} onPaste={onPasteMain} className={inputCls(errors.image) + ' flex-1'} placeholder="https://..., Ctrl+V ảnh, hoặc Upload" />
                <label className="h-11 px-4 rounded-md border border-border bg-surface hover:bg-brand-50 hover:border-brand-500 cursor-pointer inline-flex items-center gap-2 text-sm font-medium text-text whitespace-nowrap transition">
                  {uploadingMain ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Upload
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleMainUpload(e.target.files?.[0])} disabled={uploadingMain} />
                </label>
              </div>
              {errors.image && <div className="text-xs text-danger mt-1">{errors.image}</div>}
            </label>

            {v.image ? (
              <div
                className="aspect-[4/3] rounded-lg bg-slate-100 border-2 border-dashed border-border overflow-hidden relative group cursor-pointer focus:outline-none focus:border-brand-500"
                tabIndex={0}
                onPaste={onPasteMain as any}
              >
                <img
                  src={v.image}
                  alt="preview"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 4 3"><rect width="4" height="3" fill="%23e2e8f0"/></svg>'
                  }}
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-white text-sm font-semibold">
                  {uploadingMain ? 'Đang upload...' : 'Click vào đây → Ctrl+V để đổi ảnh'}
                </div>
              </div>
            ) : (
              <div
                className="aspect-[4/3] rounded-lg bg-brand-50/50 border-2 border-dashed border-brand-300 flex flex-col items-center justify-center text-brand-600 cursor-pointer hover:bg-brand-50 focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
                tabIndex={0}
                onPaste={onPasteMain as any}
              >
                {uploadingMain ? (
                  <Loader2 className="w-10 h-10 animate-spin" />
                ) : (
                  <>
                    <Upload className="w-10 h-10 mb-2" />
                    <div className="font-semibold text-sm">Click vào đây rồi Ctrl+V</div>
                    <div className="text-xs text-muted mt-1">Hoặc bấm nút Upload ở trên</div>
                  </>
                )}
              </div>
            )}

            <label className="block">
              <div className="text-sm font-medium text-text mb-1.5">Ảnh phụ (mỗi dòng 1 URL)</div>
              <textarea
                value={v.imagesText}
                onChange={(e) => upd('imagesText', e.target.value)} onPaste={onPasteExtra}
                rows={5}
                className="w-full rounded-md border border-border bg-surface px-3.5 py-2.5 text-sm font-mono outline-none focus:border-brand-500 resize-y"
                placeholder={`https://.../anh-1.jpg\nhttps://.../anh-2.jpg`}
              />
              <div className="text-xs text-muted mt-1 inline-flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5" /> Dùng URL ảnh ngoài (Unsplash, CDN...)</div>
            </label>
          </section>
        </div>
      </div>
    </form>
  )
}
